// src/MathTools/SummationTool.js
import React, { useState } from "react";
import CoordinatePlane from "./CoordinatePlane"; // adjust path as needed

/**
 * SummationTool
 * 
 * Props:
 *   initialExpression: string
 *   startIndex: number
 *   endIndex: number
 *   showGraph: boolean — if true, show a side-by-side graph of the terms
 */
export default function SummationTool({
  initialExpression = "a1 + (k-1)*d",
  startIndex = 1,
  endIndex = 5,
  showGraph = false
}) {
  const [expression, setExpression] = useState(initialExpression);
  const [a1, setA1] = useState(2);
  const [d, setD] = useState(3);
  const [kStart, setKStart] = useState(startIndex);
  const [kEnd, setKEnd] = useState(endIndex);

  // Evaluate terms
  const terms = [];
  for (let k = kStart; k <= kEnd; k++) {
    const context = { k, a1, d, Math };
    let val = 0;
    try {
      val = evaluateExpression(expression, context);
    } catch (err) {
      val = NaN;
    }
    terms.push({ k, value: val });
  }

  const sum = terms.reduce((acc, t) => acc + t.value, 0);

  // If we show a graph, we'll turn each term into a "point" in lines array
  // For an x-axis of [kStart..kEnd], y-axis of [minTerm..maxTerm]
  let lines = [];
  let xMin = kStart, xMax = kEnd;
  let yMin = 0, yMax = 10; // default

  if (showGraph && terms.length > 0) {
    // Possibly set yMin to the smallest value (or zero if you want)
    // and yMax to largest + 10% margin
    const minVal = Math.min(...terms.map(t => t.value), 0);
    const maxVal = Math.max(...terms.map(t => t.value), 0);
    yMin = Math.floor(minVal) - 1;
    yMax = Math.ceil(maxVal) + 1;

    // build "point lines" => start=end => a circle
    lines = terms.map(t => ({
      start: [t.k, t.value],
      end: [t.k, t.value],
      color: "blue",
      label: `k=${t.k}`
    }));
  }

  return (
    <div style={{
      border: "1px solid #ccc",
      padding: "16px",
      borderRadius: 6,
      marginTop: 16,
      display: "flex",
      gap: "2rem",
      alignItems: "flex-start"   // ensures both panels align at the top
    }}>
      {/* Left Panel: numeric UI */}
      <div style={{ flex: "1 1 0", minWidth: 220 }}>
        <h4 style={{ marginTop: 0 }}>Summation Tool</h4>

        {/* Expression + param inputs */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: "block", marginBottom: 6 }}>
            Expression (in terms of k, a1, d):
            <input
              type="text"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              style={{ width: "35%", marginTop: 4 }}
            />
          </label>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <label>
              a1:
              <input
                type="number"
                value={a1}
                onChange={(e) => setA1(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label>
              d:
              <input
                type="number"
                value={d}
                onChange={(e) => setD(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label>
              Start k:
              <input
                type="number"
                value={kStart}
                onChange={(e) => setKStart(parseInt(e.target.value) || 1)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label>
              End k:
              <input
                type="number"
                value={kEnd}
                onChange={(e) => setKEnd(parseInt(e.target.value) || 1)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
          </div>
        </div>

        {/* Terms list */}
        <div>
          <strong>Terms:</strong>
          <ul>
            {terms.map((t, i) => (
              <li key={i}>
                k={t.k}: {t.value.toFixed(3)}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ marginTop: 8 }}>
          <strong>Sum:</strong> {sum.toFixed(3)}
        </div>
      </div>

      {/* Right Panel: the graph (if showGraph) */}
      {showGraph && (
        <div style={{ flex: "1 1 0" }}>
          <h3 style={{ marginTop: 0}}>Term Plot</h3>
          <CoordinatePlane
            width={450}
            height={450}
            showAxes={true}
            lines={lines}
            xDomain={[xMin, xMax]}
            yDomain={[yMin, yMax]}
            axisMode="origin"
            disablePlot={true}
          />
          <p style={{ fontSize: "0.9em" }}>
            Plot of each term: (k, value)
          </p>
        </div>
      )}
    </div>
  );
}

/** 
 * evaluateExpression - naive approach (uses new Function).
 * In production, consider mathjs or a real parser for safety.
 */

function evaluateExpression(expr, context) {
  // eslint-disable-next-line no-new-func
  const func = new Function(...Object.keys(context), `return ${expr};`);
  return func(...Object.values(context));
}
